import React from 'react';
/** Generic skeleton block */
function Bone({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`bg-slate-800 rounded animate-pulse ${className}`} data-id="element-2901" />;
}
/** Sprint tab skeleton */
export function SprintSkeleton() {
  return <div className="flex-1 overflow-y-auto p-8 animate-in fade-in duration-200" data-id="element-2902">
      <div className="max-w-5xl mx-auto" data-id="element-2903">
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-id="element-2904">
          {/* Header */}
          <div className="bg-slate-950 p-6 border-b border-slate-800" data-id="element-2905">
            <div className="flex justify-between items-center" data-id="element-2906">
              <div className="space-y-2" data-id="element-2907">
                <Bone className="h-7 w-64" data-id="element-2908" />
                <Bone className="h-4 w-96" data-id="element-2909" />
              </div>
              <div className="text-right space-y-2" data-id="element-2910">
                <Bone className="h-4 w-40 ml-auto" data-id="element-2911" />
                <Bone className="h-6 w-20 ml-auto rounded-full" data-id="element-2912" />
              </div>
            </div>
          </div>
          {/* Issues */}
          <div className="p-6 space-y-3" data-id="element-2913">
            <div className="flex items-center gap-2 mb-4" data-id="element-2914">
              <Bone className="h-5 w-32" data-id="element-2915" />
              <Bone className="h-4 w-24" data-id="element-2916" />
            </div>
            {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-950/50 border border-slate-800" data-id="element-2917">
                <Bone className="w-5 h-5 rounded shrink-0" data-id="element-2918" />
                <Bone className="h-4 w-16 shrink-0" data-id="element-2919" />
                <Bone className="h-4 flex-1" data-id="element-2920" />
                <Bone className="h-5 w-12 rounded-full shrink-0" data-id="element-2921" />
                <Bone className="w-6 h-6 rounded-full shrink-0" data-id="element-2922" />
                <Bone className="h-4 w-8 shrink-0" data-id="element-2923" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
/** Backlog tab skeleton */
export function BacklogSkeleton() {
  return <div className="flex-1 overflow-y-auto p-8 animate-in fade-in duration-200" data-id="element-2924">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-2925">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-2926">
          <Bone className="h-8 w-48" data-id="element-2927" />
          <Bone className="h-8 w-24 rounded-lg" data-id="element-2928" />
        </div>
        {/* Stats */}
        <div className="grid grid-cols-5 gap-4" data-id="element-2929">
          {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2" data-id="element-2930">
              <Bone className="h-3 w-20" data-id="element-2931" />
              <Bone className="h-8 w-12" data-id="element-2932" />
            </div>)}
        </div>
        {/* Controls */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4" data-id="element-2933">
          <Bone className="h-10 w-full rounded-lg" data-id="element-2934" />
          <div className="flex gap-3" data-id="element-2935">
            {Array.from({
            length: 4
          }).map((_, i) => <Bone key={i} className="h-8 w-28 rounded-lg" data-id="element-2936" />)}
          </div>
        </div>
        {/* Issues */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-id="element-2937">
          <div className="p-3.5 border-b border-slate-800 flex justify-between" data-id="element-2938">
            <Bone className="h-5 w-32" data-id="element-2939" />
            <Bone className="h-4 w-24" data-id="element-2940" />
          </div>
          <div className="p-3 space-y-2" data-id="element-2941">
            {Array.from({
            length: 8
          }).map((_, i) => <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-950/50 border border-slate-800" data-id="element-2942">
                <Bone className="w-5 h-5 rounded shrink-0" data-id="element-2943" />
                <Bone className="h-4 w-16 shrink-0" data-id="element-2944" />
                <Bone className="h-4 flex-1" data-id="element-2945" />
                <Bone className="h-5 w-14 rounded-full shrink-0" data-id="element-2946" />
                <Bone className="w-6 h-6 rounded-full shrink-0" data-id="element-2947" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
/** Kanban board skeleton */
export function KanbanSkeleton() {
  return <div className="h-full overflow-hidden flex flex-col animate-in fade-in duration-200" data-id="element-2948">
      {/* Header */}
      <div className="px-8 py-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between shrink-0" data-id="element-2949">
        <div className="flex items-center gap-3" data-id="element-2950">
          <Bone className="h-4 w-48" data-id="element-2951" />
        </div>
        <div className="flex items-center gap-2" data-id="element-2952">
          {Array.from({
          length: 3
        }).map((_, i) => <Bone key={i} className="w-7 h-7 rounded-full" data-id="element-2953" />)}
        </div>
      </div>
      {/* Columns */}
      <div className="flex-1 overflow-hidden" data-id="element-2954">
        <div className="flex gap-4 p-6 h-full" data-id="element-2955">
          {['Por Hacer', 'En Progreso', 'En Revisión', 'Completado'].map(label => <div key={label} className="flex flex-col w-72 shrink-0" data-id="element-2956">
                <div className="flex items-center gap-2 mb-3 px-1" data-id="element-2957">
                  <Bone className="h-4 w-24" data-id="element-2958" />
                  <Bone className="h-5 w-6 rounded-full" data-id="element-2959" />
                </div>
                <div className="flex-1 rounded-xl bg-slate-900/30 p-2 space-y-2" data-id="element-2960">
                  {Array.from({
              length: Math.floor(Math.random() * 3) + 2
            }).map((_, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-lg p-3 space-y-2" data-id="element-2961">
                      <div className="flex items-center gap-1.5" data-id="element-2962">
                        <Bone className="w-3 h-3 rounded" data-id="element-2963" />
                        <Bone className="h-3 w-14" data-id="element-2964" />
                      </div>
                      <Bone className="h-4 w-full" data-id="element-2965" />
                      <Bone className="h-4 w-3/4" data-id="element-2966" />
                      <div className="flex items-center justify-between pt-1" data-id="element-2967">
                        <div className="flex items-center gap-1.5" data-id="element-2968">
                          <Bone className="w-1.5 h-1.5 rounded-full" data-id="element-2969" />
                          <Bone className="h-3 w-6" data-id="element-2970" />
                        </div>
                        <Bone className="w-6 h-6 rounded-full" data-id="element-2971" />
                      </div>
                    </div>)}
                </div>
              </div>)}
        </div>
      </div>
    </div>;
}
/** Docs/Confluence skeleton */
export function DocsSkeleton() {
  return <div className="flex-1 flex h-full overflow-hidden bg-slate-950 animate-in fade-in duration-200" data-id="element-2972">
      {/* Sidebar */}
      <div className="w-64 border-r border-slate-800 bg-slate-900/30 flex flex-col shrink-0" data-id="element-2973">
        <div className="p-4 border-b border-slate-800" data-id="element-2974">
          <Bone className="h-5 w-24" data-id="element-2975" />
        </div>
        <div className="p-2 space-y-1" data-id="element-2976">
          {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-3 p-2" data-id="element-2977">
              <Bone className="w-6 h-6 rounded" data-id="element-2978" />
              <Bone className="h-4 flex-1" data-id="element-2979" />
            </div>)}
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0" data-id="element-2980">
        <div className="p-6 border-b border-slate-800 bg-slate-900/50" data-id="element-2981">
          <Bone className="h-7 w-48 mb-4" data-id="element-2982" />
          <Bone className="h-10 w-full rounded-xl" data-id="element-2983" />
        </div>
        <div className="flex-1 p-6" data-id="element-2984">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-2985">
            {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4 h-36 space-y-2" data-id="element-2986">
                <Bone className="h-5 w-3/4" data-id="element-2987" />
                <Bone className="h-3 w-full" data-id="element-2988" />
                <Bone className="h-3 w-5/6" data-id="element-2989" />
                <div className="flex items-center justify-between pt-4 mt-auto border-t border-slate-800/50" data-id="element-2990">
                  <div className="flex items-center gap-2" data-id="element-2991">
                    <Bone className="w-5 h-5 rounded-full" data-id="element-2992" />
                    <Bone className="h-3 w-16" data-id="element-2993" />
                  </div>
                  <Bone className="h-3 w-16" data-id="element-2994" />
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}